/// <reference path="./assets.d.ts" />
import type { BotAvatarSet } from "@orbin/contracts";
import { parseBotAvatarValue } from "@orbin/contracts";
import { avatarIdentitySeed, organicAvatarPath } from "@orbin/core";
import headImage from "@orbin/ui-tokens/mascot/head-small.webp";
import { memo, useMemo } from "react";
import { botIdentityColor } from "./bot-dot.js";
import { cn } from "./lib/utils.js";

export interface BotAvatarProps {
  /** The stored avatar value: a hex colour, an encoded shape, or an uploaded picture. */
  color: string;
  /** Per-bot set; missing means the original organic body. */
  avatarSet?: BotAvatarSet | undefined;
  /** The bot's id, which seeds its colour and organic body. */
  identity?: string | undefined;
  size?: number;
  className?: string;
}

/** The mascot's flat head, drawn inline so one colour disc can carry it at small sizes. */
function MascotHead() {
  return (
    <svg viewBox="0 0 100 100" className="size-[82%]" aria-hidden="true">
      <circle cx="21" cy="27" r="15" fill="#8B5433"/>
      <circle cx="79" cy="27" r="15" fill="#8B5433"/>
      <circle cx="21.5" cy="28" r="8.5" fill="#EA8167"/>
      <circle cx="78.5" cy="28" r="8.5" fill="#EA8167"/>
      <path d="M43 17 L45 6 L50 13 L55 6 L57 17 Z" fill="#85552F"/>
      <ellipse cx="50" cy="56" rx="39" ry="37" fill="#85552F"/>
      <ellipse cx="50" cy="63" rx="31" ry="28" fill="#DBA574"/>
      <path d="M27 40 Q35 33 43 39 M57 39 Q65 33 73 40" fill="none" stroke="#3A2113" strokeWidth="3.6" strokeLinecap="round"/>
      <circle cx="35" cy="54" r="8" fill="#1B1210"/>
      <circle cx="65" cy="54" r="8" fill="#1B1210"/>
      <circle cx="37.6" cy="51" r="2.8" fill="#fff"/>
      <circle cx="67.6" cy="51" r="2.8" fill="#fff"/>
      <ellipse cx="50" cy="67" rx="10.5" ry="8" fill="#5A331A"/>
      <path d="M41 79 Q50 86 59 79" fill="none" stroke="#2B1710" strokeWidth="2.8" strokeLinecap="round"/>
    </svg>
  );
}

function OrganicBody({ hex, seed }: { hex: string; seed: number }) {
  const path = useMemo(() => organicAvatarPath(seed), [seed]);
  return (
    <svg viewBox="-60 -60 120 120" className="size-full overflow-visible" aria-hidden="true">
      <path d={path} fill={hex} />
      <g transform={`rotate(${(seed % 9) - 4})`} fill="#111">
        <rect x="-14" y="-12" width="7" height="24" rx="3.5" />
        <rect x="7" y="-12" width="7" height="24" rx="3.5" />
      </g>
    </svg>
  );
}

/**
 * A teammate's avatar: its uploaded picture, or its set's drawing in its own colour. The Preá
 * set puts the mascot on a disc of the bot's colour; the original set draws the organic body
 * its id picks.
 */
export const BotAvatar = memo(function BotAvatar({
  color,
  avatarSet,
  identity = "",
  size = 20,
  className,
}: BotAvatarProps) {
  const hex = useMemo(() => botIdentityColor(color, identity), [color, identity]);
  const image = parseBotAvatarValue(color);
  const style = { width: size, height: size };
  const shared = cn("inline-flex shrink-0 select-none items-center justify-center", className);
  if (image.kind === "image") {
    return (
      <span aria-hidden="true" data-bot-avatar="image" className={shared} style={style}>
        <img src={image.imageUrl} alt="" className="size-full rounded-full object-cover" />
      </span>
    );
  }
  if (avatarSet === "prea") {
    return (
      <span
        aria-hidden="true"
        data-bot-avatar="prea"
        className={cn(shared, "rounded-full")}
        style={{ ...style, backgroundColor: hex }}
      >
        {size <= 32 ? (
          <MascotHead />
        ) : (
          <img src={headImage} alt="" draggable={false} className="size-[88%] object-contain" />
        )}
      </span>
    );
  }
  return (
    <span aria-hidden="true" data-bot-avatar="organic" className={shared} style={style}>
      <OrganicBody hex={hex} seed={avatarIdentitySeed(identity || hex)} />
    </span>
  );
});

/** A group's members as overlapping avatars. */
export function BotAvatarStack({
  members,
  size = 16,
  max = 3,
  className,
}: {
  members: readonly {
    botId?: string | undefined;
    color: string;
    avatarSet?: BotAvatarSet | undefined;
  }[];
  size?: number;
  max?: number;
  className?: string;
}) {
  return (
    <span aria-hidden="true" className={cn("inline-flex shrink-0 items-center", className)}>
      {members.slice(0, max).map((member, index) => (
        <BotAvatar
          key={member.botId ?? index}
          color={member.color}
          avatarSet={member.avatarSet}
          identity={member.botId}
          size={size}
          className={cn(index > 0 && "-ms-1.5", "rounded-full ring-2 ring-background")}
        />
      ))}
    </span>
  );
}


